import React, { useReducer, useEffect, useCallback, useMemo, useContext } from 'react';
import _ from 'lodash';
import { getPackage, isPackageExpired } from 'services';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import useLocalStorageState from 'hooks/UseLocalStorageState';
import { ConfigContext } from 'contexts/ConfigContext';

// Action Types
export const PACKAGE_ACTIONS = {
    SET_PACKAGE: 'SET_PACKAGE',
    SET_FETCHING: 'SET_FETCHING',
    ADD_SHORTLIST_ITEM: 'ADD_SHORTLIST_ITEM',
    REMOVE_SHORTLIST_ITEM: 'REMOVE_SHORTLIST_ITEM',
    SET_VIEW: 'SET_VIEW',
    SET_MESSAGE_DRAWER_OPEN: 'SET_MESSAGE_DRAWER_OPEN'
};

const DEFAULT_STATE = {
    currentPack: null,
    isFetching: false,
    view: "Package",
    messageDrawerOpen: false,
};

// Reducer
const packageReducer = (state, action) => {
    switch (action.type) {
        case PACKAGE_ACTIONS.SET_PACKAGE:
            return {
                ...state,
                currentPack: action.payload
            };
        case PACKAGE_ACTIONS.SET_FETCHING:
            return {
                ...state,
                isFetching: action.payload
            };
        case PACKAGE_ACTIONS.ADD_SHORTLIST_ITEM:
            if (!state.currentPack) return state;
            const packFavourites = [...state.currentPack.Favourites];
            if (packFavourites.some(item => item.ModelId === action.payload.ModelId)) return state;
            return {
                ...state,
                currentPack: {
                    ...state.currentPack,
                    Favourites: [...packFavourites, action.payload]
                }
            };
        case PACKAGE_ACTIONS.REMOVE_SHORTLIST_ITEM:
            if (!state.currentPack) return state;

            const newFavourites = state.currentPack.Favourites.filter(
                item => item.PackageModelId !== action.payload
            );
            let newView = state.view;
            if (newFavourites.length === 0) {
                newView = "Package";
            }

            return {
                ...state,
                currentPack: {
                    ...state.currentPack,
                    Favourites: newFavourites
                },
                view: newView
            };
        case PACKAGE_ACTIONS.SET_VIEW:
            return {
                ...state,
                view: action.payload
            };
        case PACKAGE_ACTIONS.SET_MESSAGE_DRAWER_OPEN:
            return {
                ...state,
                messageDrawerOpen: action.payload
            };
        default:
            return state;
    }
};

const PackageStateContext = React.createContext(DEFAULT_STATE);
const PackageDispatchContext = React.createContext(null);

// Custom hooks to use the context
export const usePackage = () => {
    const context = useContext(PackageStateContext);
    if (context === undefined) {
        throw new Error('usePackage must be used within a PackageContextProvider');
    }
    return context;
};

export const usePackageDispatch = () => {
    const dispatch = useContext(PackageDispatchContext);
    if (dispatch === undefined) {
        throw new Error('usePackageDispatch must be used within a PackageContextProvider');
    }
    return dispatch;
};

const PackageContextProvider = ({ packageCode, pack, children }) => {
    const [state, dispatch] = useReducer(packageReducer, DEFAULT_STATE);
    const [persistedView, setPersistedView] = useLocalStorageState('package_view', DEFAULT_STATE.view);
    const history = useHistory();
    const { applyDepartmentOverrides } = useContext(ConfigContext);

    // Initialize view from persisted value
    useEffect(() => {
        if (persistedView) {
            dispatch({ type: PACKAGE_ACTIONS.SET_VIEW, payload: persistedView });
        }
    }, []);

    // Applica gli override del dipartimento quando il package viene caricato
    useEffect(() => {
        if (state.currentPack && state.currentPack.DepartmentCreated) {
            applyDepartmentOverrides(state.currentPack.DepartmentCreated);
        }
    }, [state.currentPack, applyDepartmentOverrides]);

    // Update persisted view when state.view changes
    useEffect(() => {
        if (state.view !== persistedView) {
            setPersistedView(state.view);
        }
    }, [state.view]);

    useEffect(() => {
        //if there are no models in the favourites, set the view to package
        if (state.currentPack && state.currentPack.Favourites.length === 0) {
            dispatch({ type: PACKAGE_ACTIONS.SET_VIEW, payload: "Package" });
        }
    }, [state.currentPack]);

    const getPackageByCode = async (code) => {
        dispatch({ type: PACKAGE_ACTIONS.SET_FETCHING, payload: true });
        try {
            const response = await getPackage(code);
            if (!response) {
                dispatch({ type: PACKAGE_ACTIONS.SET_FETCHING, payload: false });
                history.push({
                    pathname: `/error/invalid`,
                    state: null
                });
            }
            if (isPackageExpired(response)) {
                dispatch({ type: PACKAGE_ACTIONS.SET_FETCHING, payload: false });
                history.push({
                    pathname: `/error/expired`,
                    state: null
                });
            } else {
                const currentpackage = { ...response };
                for (const model of currentpackage.Models) {
                    model.Playlists = _.sortBy(model.Playlists, 'PlaylistOrder');
                }
                currentpackage.Models = _.sortBy(currentpackage.Models, 'Order');
                dispatch({ type: PACKAGE_ACTIONS.SET_PACKAGE, payload: currentpackage });
            }
        } catch (error) {
            switch (error.response.status) {
                case 404:
                    history.push({
                        pathname: `/error/invalid`,
                        state: null
                    });
                    break;
                case 400:
                    if (error.response.data === "Package expired") {
                        history.push({
                            pathname: `/error/expired`,
                            state: null
                        });
                    } else {
                        history.push({
                            pathname: `/error/invalid`,
                            state: null
                        });
                    }
                    break;
                default:
                    history.push({
                        pathname: `/error/unavailable`,
                        state: null
                    });
                    break;
            }
        }
        dispatch({ type: PACKAGE_ACTIONS.SET_FETCHING, payload: false });
    };

    useEffect(() => {
        if (pack) {
            dispatch({ type: PACKAGE_ACTIONS.SET_PACKAGE, payload: pack });
        } else {
            getPackageByCode(packageCode);
        }
    }, [packageCode, pack]);

    const isDirectLink = useCallback(() => {
        if (!state.currentPack) return false;
        const sender = state.currentPack.Booker ? (state.currentPack.Booker.SenderEmail ? state.currentPack.Booker.SenderEmail : state.currentPack.Booker.Email) : null;
        const recipient = state.currentPack.RecipientEmail ? state.currentPack.RecipientEmail : null;
        return sender && recipient && sender === recipient;
    }, [state.currentPack]);

    const isInteractive = useCallback(() => {
        if (!state.currentPack) return false;
        return state.currentPack.isInteractive;
    }, [state.currentPack]);

    const PackageContextValue = useMemo(() => {
        return {
            currentPack: state.currentPack,
            packageCode: packageCode,
            isFetching: state.isFetching,
            useShortlist: !isDirectLink() && isInteractive(),
            canSendShortlist: true,
            view: state.view,
            messageDrawerOpen: state.messageDrawerOpen
        }
    }, [state.currentPack, state.isFetching, state.view, state.messageDrawerOpen, packageCode, isDirectLink, isInteractive]);

    return (
        <PackageStateContext.Provider value={PackageContextValue}>
            <PackageDispatchContext.Provider value={dispatch}>
                {children}
            </PackageDispatchContext.Provider>
        </PackageStateContext.Provider>
    )
};

export { PackageContextProvider };

